import moment from "moment";
import React, { useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { BadRequestError } from "../../api";
import {
  apiShape,
  backendShape,
  useBrowser,
  useNotifier,
  useRules,
  withApi,
  withBackend,
} from "../../context";
import ActionBar from "../ActionBar/ActionBar";
import { Column, Container, Row } from "../Grid";
import Heading from "../Heading";
import Loader from "../Loader";
import AssetForm from "./AssetForm";

import SpecificationKeys from "../../constants/SpecificationKeys";
import { isUserInGroup } from "../../extractors";
import { accessRightsShape, userShape } from "../../shapes";
import {
  convertLabelToSpecificationKey,
  convertSpecificationKeyToLabel,
  createEnforcedTenant,
  extractCode,
  isEmpty,
  isFwr30v10Device,
  isNotEmpty,
  isSomething,
} from "../../utils";
import * as gpsUtils from "../../utils/gpsUtils";
import {
  enforceAccessRightCanUpdate,
  withAccessRights,
  withUser,
} from "../../wrappers";
import BackButton from "../BackButton";
import PermissionsEdit from "../Permissions/PermissionsEdit";
import EditPictures from "../Pictures/EditPictures";
import { PROJECT_ENGINEER_ROLE } from "../Specifications/SpecificationConstants";
import AssetAddOns from "./AssetAddOns";
import ConnectedAssetActivation from "./ConnectedAssetActivation";
import ConnectedAssetSubscriptionClue from "./ConnectedAssetSubscriptionClue";

interface ISpecifications {
  [key: string]: {
    value: string | number | boolean;
    ui_visible?: boolean;
  };
}

interface IFormSpecification {
  key: {
    id?: string;
    name?: string;
  };
  uiVisible?: boolean;
  value?: string | number | boolean;
}

function assetRules(rules: $TSFixMe, values: $TSFixMe) {
  return rules.asset({
    manufacturerName: values.manufacturer.name,
    productCode: values.product.code,
    productTenantPublic: values.manufacturer.tenantPublic,
    extendedOrderCode: values.extendedOrderCode, // required to detect FWR30v10 device
  });
}

type Props = {
  readonly user: userShape;
  readonly api: apiShape;
  readonly backend: backendShape;
  readonly accessRights: accessRightsShape;
};

export function AssetEdit({ user, api, backend, accessRights }: Props) {
  const intl = useIntl();
  const rules = useRules();
  const browser = useBrowser();
  const notifier = useNotifier();
  const params = useParams<{ id: string }>();

  const [initialValues, setInitialValues] = useState();
  const [formValues, setFormValues] = useState();
  const [subscriptionValid, setSubscriptionValid] = useState();
  const [showConnectedAssetActivation, setShowConnectedAssetActivation] =
    useState();
  const [gpsDevSettings, setGPSDevSettings] = useState(false);
  const [gpsSpecAvailable, setGPSSpecAvailable] = useState(false);
  const initialSpecificationKeys = useRef([]);
  const specifications: ISpecifications = {};

  const isUserProjectEngineer = isUserInGroup(user, PROJECT_ENGINEER_ROLE);

  // eslint-disable-next-line camelcase
  const getExtendedOrderCode = async (order_code: $TSFixMe) => {
    try {
      const response = await api.get("/endress/extended_order_code_lookup", {
        // eslint-disable-next-line camelcase
        order_code,
      });
      return response;
    } catch {
      return null;
    }
  };

  const setInitialGPSValues = (
    values: $TSFixMe,
    response: $TSFixMe,
    showMeasuringInterval: $TSFixMe,
  ) => {
    const needsGPSInterval = assetRules(rules, values).get("needsGPSInterval");
    const gpsDevSettingsValue =
      response.specifications[SpecificationKeys.GPSSettings.GPS_DEV_SETTINGS]
        ?.value === "true";
    setGPSDevSettings(gpsDevSettingsValue);
    const basicSettings =
      showMeasuringInterval &&
      needsGPSInterval &&
      isSomething(values.transmissionInterval);
    const enableGPSSettings = assetRules(rules, values).get(
      "enableGPSForOrderCode",
      basicSettings,
      values.extendedOrderCode,
      gpsDevSettingsValue,
    );

    if (enableGPSSettings) {
      const gpsSendInterval =
        response.specifications[
          SpecificationKeys.GPSSettings.GPS_SEND_INTERVAL
        ];
      const gpsSendLatestInterval =
        response.specifications[
          SpecificationKeys.GPSSettings.GPS_SEND_LATEST_INTERVAL
        ];
      const gpsAllIntervalOptions = assetRules(rules, values).get(
        "gpsIntervalOptions",
        intl,
      );
      const gpsIntervalOptions = gpsAllIntervalOptions?.find(
        (label: $TSFixMe) => label.name === values.transmissionInterval?.name,
      )?.gpsOptions;

      let gpsInterval;
      let gpsActive;
      if (gpsSendInterval && gpsSendLatestInterval) {
        setGPSSpecAvailable(true);
        if (Number(gpsSendInterval.value) !== 0) {
          gpsInterval = {
            id: gpsSendInterval.value,
            name: gpsIntervalOptions.find(
              (label: $TSFixMe) => label.id === gpsSendInterval.value,
            ).name,
          };
          gpsActive = true;
        } else {
          gpsInterval =
            gpsSendLatestInterval.value !== "0"
              ? {
                  id: gpsSendLatestInterval.value,
                  name: gpsIntervalOptions.find(
                    (label: $TSFixMe) =>
                      label.id === gpsSendLatestInterval.value,
                  ).name,
                }
              : null;
          gpsActive = false;
        }
      }
      return { gpsInterval, gpsActive };
    }
    return undefined;
  };

  const setGPSValuesOnSubmit = async (values: $TSFixMe) => {
    const showMeasuringInterval = assetRules(rules, values).get(
      "needsMeasuringInterval",
    );
    const needsGPSInterval = assetRules(rules, values).get("needsGPSInterval");
    const basicSettings =
      showMeasuringInterval &&
      needsGPSInterval &&
      isSomething(values.transmissionInterval);
    const enableGPSSettings = assetRules(rules, values).get(
      "enableGPSForOrderCode",
      basicSettings,
      values.extendedOrderCode,
      gpsDevSettings,
    );

    if (enableGPSSettings) {
      const gpsSpecifications = gpsUtils.setGPSInterval(values);
      // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
      specifications[[SpecificationKeys.GPSSettings.GPS_SEND_INTERVAL]] =
        // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
        gpsSpecifications[[SpecificationKeys.GPSSettings.GPS_SEND_INTERVAL]];
      // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
      specifications[[SpecificationKeys.GPSSettings.GPS_SEND_LATEST_INTERVAL]] =
        gpsSpecifications[
          // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
          [SpecificationKeys.GPSSettings.GPS_SEND_LATEST_INTERVAL]
        ];
    } else if (gpsSpecAvailable) {
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      await api.delete(`/assets/${initialValues.id}/specifications`, [
        "eh.user_config.gps_send_interval",
        "eh.user_config.latest_gps_send_interval",
      ]);
    }
  };

  const setManualGPSEditSpecification = async (
    currentSpecifications: IFormSpecification[],
    initialSpecifications: IFormSpecification[],
  ) => {
    const currentLatitudeValue = currentSpecifications.find(
      (specs) =>
        specs.key.id === SpecificationKeys.LocalizationSettings.LATITUDE,
    )?.value;
    const currentLongitudeValue = currentSpecifications.find(
      (specs) =>
        specs.key.id === SpecificationKeys.LocalizationSettings.LONGITUDE,
    )?.value;
    const initialLatitudeValue = initialSpecifications.find(
      (specs) =>
        specs.key.id === SpecificationKeys.LocalizationSettings.LATITUDE,
    )?.value;
    const initialLongitudeValue = initialSpecifications.find(
      (specs) =>
        specs.key.id === SpecificationKeys.LocalizationSettings.LONGITUDE,
    )?.value;

    if (
      currentLatitudeValue !== initialLatitudeValue ||
      currentLongitudeValue !== initialLongitudeValue
    ) {
      specifications[
        SpecificationKeys.LocalizationSettings.LAST_USER_GPS_UPDATE
      ] = {
        value: moment.utc().toISOString(),
        ui_visible: false,
      };
    }

    return null;
  };

  const handleError = async (error: $TSFixMe, actions: $TSFixMe) => {
    if (
      error instanceof BadRequestError &&
      // @ts-expect-error TS(2345) FIXME: Argument of type '"serial_number"' is not assignab... Remove this comment to see the full error message
      error.contains("not_unique_in_scope", "serial_number")
    ) {
      const errorMessage = error.extractError("not_unique_in_scope").message;
      const includesManufacturer = errorMessage.includes("manufacturer");
      const scope = includesManufacturer ? "manufacturer" : "product";
      const messageId = `api.error.serial_number.not_unique_in_scope_for_${scope}`;
      actions.setErrors({
        serial_number: intl.formatMessage({ id: messageId }),
      });
    } else {
      notifier.showError(api.translateError(error));
    }
  };

  const handleOnSubmit = async (values: $TSFixMe, actions: $TSFixMe) => {
    try {
      let { manufacturer } = values;
      let { product } = values;
      let { tenant } = values;

      const isEndress = manufacturer && manufacturer.name === "Endress+Hauser";

      // create tenant if needed
      // New manufacturer, user has no tenant yet
      if (!values.tenant && values.manufacturer.new) {
        tenant = {
          id: (
            await createEnforcedTenant(
              api,
              [user?.firstName, user?.lastName].join(" ").trim(),
            )
          ).id,
        };
        // Existing private manufacturer
      } else if (
        !values.manufacturer.new &&
        !values.manufacturer.tenantPublic
      ) {
        tenant = { id: values.manufacturer.tenantId };
        // Create completely new tenant
      } else if (values.tenant?.new) {
        tenant = {
          id: (await api.post("/tenants", { name: values.tenant.name })).id,
        };
      }

      // create manufacturer if needed
      if (values.manufacturer.new) {
        try {
          manufacturer = {
            ...(await api.post("/companies", {
              name: values.manufacturer.name,
              tenant: { id: tenant.id },
            })),
            tenant,
          };
        } catch (error) {
          if (error instanceof BadRequestError && error.contains("taken")) {
            actions.setErrors({
              manufacturer: intl.formatMessage({
                id: "api.error.manufacturer.taken",
              }),
            });
            return;
          }
          throw error;
        }
      }

      // create product if needed
      if (values.product.new) {
        product = {
          ...(await api.post("/products", {
            manufacturer: { id: manufacturer.id },
            name: product.name,
            product_code: product.code,
            tenant: { id: tenant.id },
          })),
          code: product.code,
          manufacturerId: manufacturer.id,
          tenant,
        };
      }

      const payload = {
        serial_number: values.serial_number,
        product: { id: product.id },
        status: { id: values.status.id },
        description: values.description,
        production_date: isNotEmpty(values.production_date)
          ? values.production_date
          : null,
        tenant,
      };

      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      await api.patch(`/assets/${initialValues.id}`, payload);
      if (values.transmissionInterval) {
        specifications[
          // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
          [SpecificationKeys.ConnectivitySettings.TRANSMISSION_INTERVAL]
        ] = {
          value: values.transmissionInterval.id,
        };
      }
      if (values.measuringInterval) {
        specifications[
          // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
          [SpecificationKeys.ConnectivitySettings.MEASURING_INTERVAL]
        ] = { value: values.measuringInterval.id };
      }
      if (values.firstMeasurement) {
        specifications[
          // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
          [SpecificationKeys.ConnectivitySettings.FIRST_MEASUREMENT]
        ] = { value: values.firstMeasurement.toISOString() };
      }

      if (isEndress && values.orderCode && values.product.code) {
        const orderCodeSpecValue = `${values.product.code}-${values.orderCode}`;
        // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
        specifications[[SpecificationKeys.GeneralSettings.ORDER_CODE]] = {
          value: orderCodeSpecValue,
        };
      }
      await setGPSValuesOnSubmit(values);

      setManualGPSEditSpecification(
        values.specifications,
        (initialValues as $TSFixMe).specifications,
      );

      values.specifications?.forEach((specification: $TSFixMe) => {
        if (specification.key !== "" && specification.key?.name) {
          specifications[
            convertLabelToSpecificationKey(specification.key.name)
          ] = {
            value: specification.value,
            ui_visible: isUserProjectEngineer
              ? !!specification.uiVisible
              : true,
          };
        }
      });
      if (Object.keys(specifications).length > 0) {
        await api.patch(
          `/assets/${
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            initialValues.id
          }/specifications`,
          specifications,
        );
      }

      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      if (isNotEmpty(initialValues.orderCode) && isEmpty(values.orderCode)) {
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        await api.delete(`/assets/${initialValues.id}/specifications`, [
          "eh.pcps.tmp.ordercode",
        ]);
      }

      if (initialSpecificationKeys.current.length > 0) {
        const specificationKeysToDelete =
          initialSpecificationKeys.current.filter(
            (key) =>
              !values.specifications.find(
                (specification: $TSFixMe) =>
                  specification.name !== "" &&
                  convertLabelToSpecificationKey(specification.key?.name) ===
                    key,
              ),
          );
        if (specificationKeysToDelete.length > 0) {
          await api.delete(
            `/assets/${
              // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
              initialValues.id
            }/specifications`,
            specificationKeysToDelete,
          );
        }
      }

      // patch on connected asset is only triggered when it is triggered from application offering connected asset addon
      if (
        assetRules(rules, values).get("connectedAsset") &&
        rules.application().get("hasConnectedAssetAddon") &&
        !(
          values.product.code === "UXTR01" ||
          isFwr30v10Device(values.product.code, values.extendedOrderCode)
        )
      ) {
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        await backend.patch(`/connected_assets/${initialValues.id}`);
      }

      notifier.showSuccess(
        intl.formatMessage({ id: "asset_edit.success_notification" }),
      );
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      browser.navigateTo(`/assets/${initialValues.id}`);
    } catch (error) {
      handleError(error, actions);
    } finally {
      actions.setSubmitting(false);
    }
  };

  React.useEffect(() => {
    const loadAsset = async () => {
      try {
        const response = await api.get(
          `/assets/${params.id}`,
          {
            include:
              "tenant,status,status.tenant,product.tenant,product.specifications,product.manufacturer,product.manufacturer.tenant,instrumentations,specifications,add_ons",
          },
          false,
        );

        const values = {
          ...response,
          manufacturer: {
            id: response.product.manufacturer.id,
            name: response.product.manufacturer.name,
            tenantId: response.product.manufacturer.tenant.id,
            tenantPublic: response.product.manufacturer.tenant.public,
            tenantName: response.product.manufacturer.tenant.name,
          },
          product: {
            id: response.product.id,
            manufacturerId: response.product.manufacturer.id,
            name: response.product.name,
            code: response.product.product_code,
            tenantId: response.product.tenant.id,
            tenantPublic: response.product.tenant.public,
            tenantName: response.product.tenant.name,
            specifications: response.product.specifications || {},
          },
          status: {
            id: response.status.id,
            name: response.status.name,
            tenantId: response.status.tenant.id,
            tenantPublic: response.status.tenant.public,
            tenantName: response.status.tenant.name,
          },
          instrumentations: {
            totalCount: response.instrumentations.total_count,
          },
          specifications: [],
        };

        if (response.specifications) {
          const deviceActiveSpec =
            response.specifications[
              // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
              [SpecificationKeys.ActivationSettings.DEVICE_ACTIVE]
            ];
          values.deviceActive = deviceActiveSpec?.value === "true"; // !! ensure deviceActive is true or false, not undefined or null
          const deviceShouldBeActiveSpec =
            response.specifications[
              // @ts-expect-error TS(2538) FIXME: Type 'string[]' cannot be used as an index type.
              [SpecificationKeys.ActivationSettings.SHOULD_BE_ACTIVE]
            ];
          values.deviceShouldBeActive =
            deviceShouldBeActiveSpec?.value === "true";
          const orderCodeSpec =
            response.specifications[
              SpecificationKeys.GeneralSettings.ORDER_CODE
            ];
          const orderCode = orderCodeSpec ? orderCodeSpec.value : null;
          values.orderCode =
            orderCode && orderCode.indexOf("-") > 0
              ? orderCode.substring(orderCode.indexOf("-") + 1)
              : null;
          const lookupResponse = await getExtendedOrderCode(orderCode);
          // eslint-disable-next-line camelcase
          values.extendedOrderCode = lookupResponse?.extended_order_code
            ? extractCode(lookupResponse.extended_order_code)
            : null;
          const showMeasuringInterval = assetRules(rules, values).get(
            "needsMeasuringInterval",
          );
          const showTransmissionInterval = assetRules(rules, values).get(
            "needsTransmissionInterval",
          );
          const showTimeOfFirstMeasurement = assetRules(rules, values).get(
            "needsTimeOfFirstMeasurement",
          );
          const measuringIntervalOptions = assetRules(rules, values).get(
            "measuringIntervalOptions",
            intl,
          );

          if (showMeasuringInterval) {
            const measuringIntervalSpec =
              response.specifications[
                SpecificationKeys.ConnectivitySettings.MEASURING_INTERVAL
              ];
            values.measuringInterval = measuringIntervalSpec
              ? measuringIntervalOptions?.find(
                  (label: $TSFixMe) => label.id === measuringIntervalSpec.value,
                )
              : null;

            const transmissionIntervalSpec =
              response.specifications[
                SpecificationKeys.ConnectivitySettings.TRANSMISSION_INTERVAL
              ];
            values.transmissionInterval =
              transmissionIntervalSpec && measuringIntervalSpec
                ? measuringIntervalOptions
                    ?.find(
                      (label: $TSFixMe) =>
                        label.id === measuringIntervalSpec.value,
                    )
                    .transmissionOptions.find(
                      (label: $TSFixMe) =>
                        label.id === transmissionIntervalSpec.value,
                    )
                : null;
          }

          if (showTransmissionInterval && !showMeasuringInterval) {
            const transmissionIntervalOptions = assetRules(rules, values).get(
              "transmissionIntervalOptions",
            );
            const transmissionIntervalSpec =
              response.specifications[
                SpecificationKeys.ConnectivitySettings.TRANSMISSION_INTERVAL
              ];
            values.transmissionInterval = transmissionIntervalSpec
              ? transmissionIntervalOptions?.find(
                  (label: $TSFixMe) =>
                    label.id === transmissionIntervalSpec.value,
                )
              : null;
            values.measuringInterval = null;
          }

          if (showTimeOfFirstMeasurement) {
            const firstMeasurementSpec =
              response.specifications[
                SpecificationKeys.ConnectivitySettings.FIRST_MEASUREMENT
              ];
            values.firstMeasurement = firstMeasurementSpec
              ? new Date(Date.parse(firstMeasurementSpec.value))
              : null;
          } else {
            values.firstMeasurement = null;
          }
          const gpsSettings = setInitialGPSValues(
            values,
            response,
            showMeasuringInterval,
          );
          if (gpsSettings) {
            values.gpsInterval = gpsSettings?.gpsInterval;
            values.gpsActive = gpsSettings?.gpsActive;
          }
          // @ts-expect-error TS(2322) FIXME: Type 'string[]' is not assignable to type 'never[]... Remove this comment to see the full error message
          initialSpecificationKeys.current = Object.keys(
            response.specifications,
          )
            .filter(
              (key) =>
                isUserProjectEngineer ||
                response.specifications[key].ui_visible,
            )
            .sort((a, b) => {
              const aVisible = response.specifications[a].ui_visible;
              const bVisible = response.specifications[b].ui_visible;

              // Sort by ui_visible first (true comes before false)
              if (aVisible !== bVisible) {
                return aVisible ? -1 : 1;
              }

              // Then sort alphabetically by key
              return a.localeCompare(b);
            });
          values.specifications = initialSpecificationKeys.current.map(
            (key) => ({
              key: { id: key, name: convertSpecificationKeyToLabel(key) },
              value: response.specifications[key].value,
              uiVisible: response.specifications[key].ui_visible,
            }),
          );
        }

        setInitialValues(values);
        setShowConnectedAssetActivation(
          assetRules(rules, values).get("showActivationStatus"),
        );
      } catch (error) {
        notifier.showError(api.translateError(error));
      }
    };

    loadAsset();
  }, [params]);

  return (
    <Loader loading={!initialValues}>
      <Container>
        <Row>
          <Column>
            <BackButton />
          </Column>
        </Row>
        <Row>
          <Column>
            <ConnectedAssetSubscriptionClue
              // @ts-expect-error TS(2322) FIXME: Type '{ initialValues: undefined; formValues: unde... Remove this comment to see the full error message
              initialValues={initialValues}
              formValues={formValues}
              setSubscriptionValid={setSubscriptionValid}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <ActionBar>
              <Heading
                title={intl.formatMessage({ id: "asset_edit.header" })}
              />
            </ActionBar>
          </Column>
        </Row>
        <Row>
          <Column lg="7">
            <Heading
              title={intl.formatMessage({ id: "asset_edit.details" })}
              level={2}
            />
            {initialValues && (
              <AssetForm
                onSubmit={handleOnSubmit}
                onChange={setFormValues}
                initialValues={initialValues}
                canSubmit={subscriptionValid}
                showAssetStatus
                gpsDevSettings={gpsDevSettings}
              />
            )}
            {initialValues && isSomething(formValues) ? (
              <AssetAddOns
                assetId={params.id}
                currentValues={formValues}
                initialValues={initialValues}
              />
            ) : null}
            {initialValues && showConnectedAssetActivation ? (
              <ConnectedAssetActivation
                assetId={params.id}
                serialNumber={(initialValues as $TSFixMe).serial_number}
                deviceActive={(initialValues as $TSFixMe).deviceActive}
                deviceShouldBeActive={
                  (initialValues as $TSFixMe).deviceShouldBeActive
                }
                hasNoInstrumentation={
                  (initialValues as $TSFixMe).instrumentations.totalCount === 0
                }
              />
            ) : null}
          </Column>
          <Column lg="5">
            {initialValues && (
              <EditPictures
                // @ts-expect-error TS(2322) FIXME: Type '{ id: string; model: string; modelId: any; p... Remove this comment to see the full error message
                id="edit-pictures"
                model="assets"
                modelId={(initialValues as $TSFixMe).id}
                placeholder="icon-eh-device"
              />
            )}
          </Column>
        </Row>
        <Row>
          <Column>
            {initialValues && (
              <PermissionsEdit
                // @ts-expect-error TS(2322) FIXME: Type '{ id: string; permitableType: string; permit... Remove this comment to see the full error message
                id="permissions"
                permitableType="Asset"
                permitableId={(initialValues as $TSFixMe).id}
                accessRights={accessRights}
                targetOnDeleteOwnReadPermission="/nodes"
              />
            )}
          </Column>
        </Row>
      </Container>
    </Loader>
  );
}

export default withBackend(
  withApi(
    withUser(withAccessRights(AssetEdit, "Asset", enforceAccessRightCanUpdate)),
  ),
);
